import { Patient } from '../components/patients/PatientCard';
import getIcon from '../utils/Icons';

export interface MedicalExamination {
  id: number;
  name: string;
  specimen_type: string;
  cost: number;
  preparation_instructions?: string;
  category?: string;
  subcategory?: string;
  is_active: boolean;
}

export interface LabRequisitionItem {
  id?: number;
  examination_id: number;
  examination?: MedicalExamination;
  special_instructions?: string;
  is_urgent: boolean;
  estimated_cost: number;
}

export interface LabRequisition {
  id?: number;
  requisition_number?: string;
  patient_id: number;
  patient?: Patient;
  doctor_id: number;
  doctor?: {
    id: number;
    name: string;
    specialty?: string;
  };
  clinic_id: number;
  appointment_id?: number;
  requisition_date: string;
  external_lab_name?: string;
  clinical_indication?: string;
  patient_instructions?: string;
  status: 'draft' | 'issued' | 'completed' | 'cancelled';
  priority: 'routine' | 'urgent' | 'stat';
  estimated_total_cost: number;
  notes?: string;
  issued_at?: string;
  completed_at?: string;
  items: LabRequisitionItem[];
  created_at?: string;
  updated_at?: string;
}

export interface CreateLabRequisitionData {
  patient_id: number;
  appointment_id?: number;
  requisition_date: string;
  external_lab_name?: string;
  clinical_indication?: string;
  patient_instructions?: string;
  priority: 'routine' | 'urgent' | 'stat';
  notes?: string;
  examinations: Array<{
    examination_id: number;
    custom_name?: string;
    special_instructions?: string;
    is_urgent: boolean;
    estimated_cost: number;
  }>;
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class LabRequisitionService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('auth_token');
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(`${LabRequisitionService.BASE_URL}${url}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API Error:', errorData);
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
}

  // Get lab requisitions with filtering
  static async getLabRequisitions(params?: {
    patient_id?: number;
    doctor_id?: number;
    status?: string;
    priority?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<LabRequisition>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `/lab-requisitions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return await this.request(url);
  }

  // Get single lab requisition
  static async getLabRequisition(id: number): Promise<LabRequisition> {
    const result = await this.request(`/lab-requisitions/${id}`);
    return result.data;
  }

  // Create new lab requisition
  static async createLabRequisition(data: CreateLabRequisitionData): Promise<LabRequisition> {
    try {
      const result = await this.request('/lab-requisitions', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      // Handle the response structure properly
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Create lab requisition error:', error);
      throw error;
    }
  }

  // Update lab requisition (draft only)
  static async updateLabRequisition(id: number, data: Partial<CreateLabRequisitionData>): Promise<LabRequisition> {
    const result = await this.request(`/lab-requisitions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return result.data;
  }

  // Delete lab requisition (draft only)
  static async deleteLabRequisition(id: number): Promise<void> {
    await this.request(`/lab-requisitions/${id}`, {
      method: 'DELETE',
    });
  }

  // Issue lab requisition
  static async issueLabRequisition(id: number): Promise<LabRequisition> {
    try {
      const result = await LabRequisitionService.request(`/lab-requisitions/${id}/issue`, {
        method: 'POST',
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Issue lab requisition error:', error);
      throw error;
    }
  }

  // Complete lab requisition
  static async completeLabRequisition(id: number): Promise<LabRequisition> {
    try {
      const result = await LabRequisitionService.request(`/lab-requisitions/${id}/complete`, {
        method: 'POST',
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Complete lab requisition error:', error);
      throw error;
    }
  }

  // Cancel lab requisition
  static async cancelLabRequisition(id: number): Promise<LabRequisition> {
    try {
      const result = await LabRequisitionService.request(`/lab-requisitions/${id}/cancel`, {
        method: 'POST',
      });
      
      if (result.success && result.data) {
        return result.data;
      } else if (result.data) {
        return result.data;
      } else {
        return result;
      }
    } catch (error) {
      console.error('Cancel lab requisition error:', error);
      throw error;
    }
  }

  // Generate PDF - Updated to use Laravel backend URL
  static async generatePDF(id: number): Promise<void> {
    // Use the Laravel backend URL instead of the frontend URL
    const backendUrl = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com';
    const printUrl = `${backendUrl.replace('/api', '')}/lab/${id}`;
    window.open(printUrl, '_blank');
  }

  // Alternative method if you want to keep the old PDF download as a separate method
  static async downloadPDF(id: number): Promise<{ pdf_url: string; pdf_path: string }> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/lab-requisitions/${id}/pdf`, {
      headers: {
        'Accept': 'application/pdf',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to generate PDF');
    }

    // Create blob URL for PDF
    const blob = await response.blob();
    const pdfUrl = URL.createObjectURL(blob);
    
    return {
      pdf_url: pdfUrl,
      pdf_path: `lab-requisition-${id}.pdf`
    };
  }

  // New method to open print view
  static openPrintView(id: number): void {
    const backendUrl = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com';
    const printUrl = `${backendUrl.replace('/api', '')}/lab/${id}`;
    window.open(printUrl, '_blank');
  }

  // Get available examinations
  static async getAvailableExaminations(): Promise<MedicalExamination[]> {
    const result = await this.request('/lab-requisitions/available-examinations');
    return result.data;
  }

  // Search examinations
  
  static async searchExaminations(query: string): Promise<MedicalExamination[]> {
  try {
    const result = await this.request(`/lab-requisitions/available-examinations?search=${encodeURIComponent(query)}`);
    
    // Handle the response structure properly
    if (result.success && result.data) {
      return Array.isArray(result.data) ? result.data : [];
    } else if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    } else {
      console.warn('Unexpected search response structure:', result);
      return [];
    }
  } catch (error) {
    console.error('Search examinations error:', error);
    throw error;
  }
}

  // Get patient's lab requisitions
  static async getPatientLabRequisitions(patientId: number): Promise<LabRequisition[]> {
  try {
    const result = await this.request(`/lab-requisitions?patient_id=${patientId}`);
    
    // Handle the response structure properly
    if (result.success && result.data) {
      return Array.isArray(result.data) ? result.data : [];
    } else if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    } else {
      console.warn('Unexpected response structure:', result);
      return [];
    }
  } catch (error) {
    console.error('Get patient lab requisitions error:', error);
    throw error;
  }
}

  // Get lab requisition statistics
  static async getStatistics(): Promise<{
    total_requisitions: number;
    draft_requisitions: number;
    issued_requisitions: number;
    completed_requisitions: number;
    cancelled_requisitions: number;
    pending_requisitions: number;
    urgent_requisitions: number;
    stat_requisitions: number;
    today_requisitions: number;
  }> {
    const result = await this.request('/lab-requisitions/statistics');
    return result.data;
  }

  // Helper methods
  static getStatusColor(status: string): string {
    switch (status) {
      case 'draft': return '#6b7280';
      case 'issued': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getStatusLabel(status: string): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'issued': return 'Issued';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return 'Unknown';
    }
  }

  static getStatusIcon(status: string): string {
    switch (status) {
      case 'draft': return '📝';
      case 'issued': return '📋';
      case 'completed': return '✅';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  }

  static getPriorityColor(priority: string): string {
    switch (priority) {
      case 'routine': return '#10b981';
      case 'urgent': return '#f59e0b';
      case 'stat': return '#ef4444';
      default: return '#6b7280';
    }
  }

  static getPriorityLabel(priority: string): string {
    switch (priority) {
      case 'routine': return 'Routine';
      case 'urgent': return 'Urgent';
      case 'stat': return 'STAT';
      default: return 'Unknown';
    }
  }

  static getPriorityIcon(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'routine': return getIcon('calendar');    // 📅 or [CAL]
    case 'urgent': return getIcon('warning');      // ⚠️ or [WARN]
    case 'stat': return getIcon('emergency');      // 🚨 or [EMRG]
    default: return getIcon('calendar');           // 📅 or [CAL]
  }
}

  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  static formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  static formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}

export default LabRequisitionService;